import { useState, useEffect, useRef } from "react"
import { Plus, Loader2, CheckCircle, AlertCircle } from "lucide-react"
import { CampaignService, CampaignTypeInfo, CampaignStatusInfo } from "@/features/campaigns"
import { HRService } from "@/features/hr"

interface AddCampaignProps {
  onCancel: () => void
  onTabChange: (tab: string) => void
  onCampaignCreated?: (campaign: any) => void
}

interface Organization {
  id: string
  name: string
  type: string
  parentId?: string
  isActive: boolean
}

export default function AddCampaign({ onCancel, onTabChange, onCampaignCreated }: AddCampaignProps) {
  const formRef = useRef<HTMLFormElement>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [organizationsLoading, setOrganizationsLoading] = useState(true)
  const [organizationsError, setOrganizationsError] = useState<string | null>(null)
  
  // Organization hierarchy state
  const [selectedEM, setSelectedEM] = useState<string>("")
  const [selectedDM, setSelectedDM] = useState<string>("")
  const [selectedCM, setSelectedCM] = useState<string>("")
  const [selectedBM, setSelectedBM] = useState<string>("")
  const [selectedAM, setSelectedAM] = useState<string>("")
  
  const [emOrganizations, setEMOrganizations] = useState<Organization[]>([])
  const [dmOrganizations, setDMOrganizations] = useState<Organization[]>([])
  const [cmOrganizations, setCMOrganizations] = useState<Organization[]>([])
  const [bmOrganizations, setBMOrganizations] = useState<Organization[]>([])
  const [amOrganizations, setAMOrganizations] = useState<Organization[]>([])
  const [campaignTypes, setCampaignTypes] = useState<CampaignTypeInfo[]>([])
  const [campaignTypesLoading, setCampaignTypesLoading] = useState(true)
  const [campaignTypesError, setCampaignTypesError] = useState<string | null>(null)
  const [campaignStatuses, setCampaignStatuses] = useState<CampaignStatusInfo[]>([])
  const [campaignStatusesLoading, setCampaignStatusesLoading] = useState(true)
  const [campaignStatusesError, setCampaignStatusesError] = useState<string | null>(null)

  // Fetch EM organizations on component mount
  useEffect(() => {
    const fetchEMOrganizations = async () => {
      try {
        setOrganizationsLoading(true)
        setOrganizationsError(null)
        const emOrgs = await HRService.getOrganizationHierarchy({ type: "EM" })
        setEMOrganizations(emOrgs)
      } catch (err: any) {
        console.error("Error fetching EM organizations:", err)
        setOrganizationsError("Failed to load EM organizations")
        setEMOrganizations([])
      } finally {
        setOrganizationsLoading(false)
      }
    }

    fetchEMOrganizations()
  }, [])

  // Fetch campaign types on component mount
  useEffect(() => {
    const fetchCampaignTypes = async () => {
      try {
        setCampaignTypesLoading(true)
        setCampaignTypesError(null)
        const response = await CampaignService.getCampaignTypes()
        setCampaignTypes(response.data || [])
      } catch (err: any) {
        console.error("Error fetching campaign types:", err)
        setCampaignTypesError("Failed to load campaign types")
        setCampaignTypes([])
      } finally {
        setCampaignTypesLoading(false)
      }
    }

    fetchCampaignTypes()
  }, [])

  // Fetch campaign statuses on component mount
  useEffect(() => {
    const fetchCampaignStatuses = async () => {
      try {
        setCampaignStatusesLoading(true)
        setCampaignStatusesError(null)
        const response = await CampaignService.getCampaignStatuses()
        setCampaignStatuses(response.data || [])
      } catch (err: any) {
        console.error("Error fetching campaign statuses:", err)
        setCampaignStatusesError("Failed to load campaign statuses")
        setCampaignStatuses([])
      } finally {
        setCampaignStatusesLoading(false)
      }
    }

    fetchCampaignStatuses()
  }, [])

  // Fetch DM organizations when EM is selected
  useEffect(() => {
    if (selectedEM) {
      const fetchDMOrganizations = async () => {
        try {
          const dmOrgs = await HRService.getOrganizationHierarchy({ 
            type: "DM", 
            parentId: selectedEM 
          })
          setDMOrganizations(dmOrgs)
          // Reset lower level selections
          setSelectedDM("")
          setSelectedCM("")
          setSelectedBM("")
          setSelectedAM("")
          setCMOrganizations([])
          setBMOrganizations([])
          setAMOrganizations([])
        } catch (err: any) {
          console.error("Error fetching DM organizations:", err)
          setDMOrganizations([])
        }
      }
      fetchDMOrganizations()
    } else {
      setDMOrganizations([])
      setSelectedDM("")
    }
  }, [selectedEM])

  // Fetch CM organizations when DM is selected
  useEffect(() => {
    if (selectedDM) {
      const fetchCMOrganizations = async () => {
        try {
          const cmOrgs = await HRService.getOrganizationHierarchy({ 
            type: "CM", 
            parentId: selectedDM 
          })
          setCMOrganizations(cmOrgs)
          // Reset lower level selections
          setSelectedCM("")
          setSelectedBM("")
          setSelectedAM("")
          setBMOrganizations([])
          setAMOrganizations([])
        } catch (err: any) {
          console.error("Error fetching CM organizations:", err)
          setCMOrganizations([])
        }
      }
      fetchCMOrganizations()
    } else {
      setCMOrganizations([])
      setSelectedCM("")
    }
  }, [selectedDM])

  // Fetch BM organizations when CM is selected
  useEffect(() => {
    if (selectedCM) {
      const fetchBMOrganizations = async () => {
        try {
          const bmOrgs = await HRService.getOrganizationHierarchy({ 
            type: "BM", 
            parentId: selectedCM 
          })
          setBMOrganizations(bmOrgs)
          // Reset lower level selections
          setSelectedBM("")
          setSelectedAM("")
          setAMOrganizations([])
        } catch (err: any) {
          console.error("Error fetching BM organizations:", err)
          setBMOrganizations([])
        }
      }
      fetchBMOrganizations()
    } else {
      setBMOrganizations([])
      setSelectedBM("")
    }
  }, [selectedCM])

  // Fetch AM organizations when BM is selected
  useEffect(() => {
    if (selectedBM) {
      const fetchAMOrganizations = async () => {
        try {
          const amOrgs = await HRService.getOrganizationHierarchy({ 
            type: "AM", 
            parentId: selectedBM 
          })
          setAMOrganizations(amOrgs)
          // Reset lower level selection
          setSelectedAM("")
        } catch (err: any) {
          console.error("Error fetching AM organizations:", err)
          setAMOrganizations([])
        }
      }
      fetchAMOrganizations()
    } else {
      setAMOrganizations([])
      setSelectedAM("")
    }
  }, [selectedBM])

  // Helper function to trim input values on blur
  const handleTrimOnBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.target.value = e.target.value.trim()
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    // Don't allow submission if data is still loading
    if (organizationsLoading || campaignTypesLoading || campaignStatusesLoading) {
      setError("Please wait for data to load before creating the campaign")
      setShowErrorModal(true)
      return
    }
    
    setLoading(true)
    setError(null)
    
    try {
    const formData = new FormData(e.currentTarget)
      
      // Get the final selected organization ID
      const finalOrganizationId = selectedAM || selectedBM || selectedCM || selectedDM || selectedEM
      
      if (!finalOrganizationId) {
        setError("Please select an organization from the hierarchy before creating the campaign")
        setShowErrorModal(true)
        setLoading(false)
        return
      }
      
      // Prepare campaign data according to API schema with trimmed values
      const campaignData = {
        name: (formData.get('name') as string)?.trim(),
        organizationId: finalOrganizationId,
        type: (formData.get('type') as "TYPE_1" | "TYPE_2" | "TYPE_3") || "TYPE_1",
        status: (formData.get('status') as "ACTIVE" | "INACTIVE" | "DISABLED" | "HIDDEN") || "ACTIVE",
        startDate: (formData.get('startDate') as string)?.trim() || undefined,
        endDate: (formData.get('endDate') as string)?.trim() || undefined,
        settings: {
          description: (formData.get('description') as string)?.trim() || '',
          targetAudience: (formData.get('targetAudience') as string)?.trim() || '',
          legalFocus: (formData.get('legalFocus') as string)?.trim() || '',
          expectedVolume: (formData.get('expectedVolume') as string)?.trim() || ''
        }
      }

      
      // Call the API
      const response = await CampaignService.createCampaign(campaignData)
      
      if (response.data) {
        setSuccess(true)
        
        // Clear the form
        formRef.current?.reset()
        
        // Reset organization hierarchy selections
        setSelectedEM("")
        setSelectedDM("")
        setSelectedCM("")
        setSelectedBM("")
        setSelectedAM("")
        setDMOrganizations([])
        setCMOrganizations([])
        setBMOrganizations([])
        setAMOrganizations([])
        
        // Call the callback if provided
        if (onCampaignCreated) {
          onCampaignCreated(response.data)
        }
        
        // Show success popup (no redirect - user stays on current page)
      }
    } catch (err: any) {
      console.error("Error creating campaign:", err)
      const errorMessage = err.message || "Failed to create campaign. Please try again."
      setError(errorMessage)
      setShowErrorModal(true)
    } finally {
      setLoading(false)
    }
  }


  return (
    <div className="max-w-4xl mx-auto">

      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <Plus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Create New Campaign</h1>
            <p className="text-gray-600 mt-1">Set up a comprehensive campaign to manage intake links, assignments, and performance tracking</p>
          </div>
        </div>
        
        {/* Progress Indicator */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            Campaign Setup
          </span>
          <span className="text-gray-300">→</span>
          <span>Configuration</span>
          <span className="text-gray-300">→</span>
          <span>Review & Launch</span>
        </div>
      </div>

      <form 
        ref={formRef}
        onSubmit={handleSubmit}
        className="space-y-8"
      >
        {/* Basic Information Section */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-xs font-bold text-blue-600">1</span>
              </div>
              Basic Information
            </h2>
            <p className="text-sm text-gray-600 mt-1">Essential details for your new campaign</p>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Campaign Name */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-900">
                Campaign Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                placeholder="e.g., Video Game Addiction Mass Tort 2024"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500 font-medium"
                onBlur={handleTrimOnBlur}
                required
              />
              <p className="text-xs text-gray-500">Choose a descriptive name that clearly identifies this campaign</p>
            </div>

            {/* Campaign Type */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900">
                Campaign Type <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                defaultValue="TYPE_1"
                required
                disabled={campaignTypesLoading}
              >
                <option value="">
                  {campaignTypesLoading ? "Loading campaign types..." : "Select Campaign Type"}
                </option>
                {campaignTypes.map((campaignType) => (
                  <option key={campaignType.type} value={campaignType.type}>
                    {campaignType.type}
                  </option>
                ))}
              </select>
              {campaignTypesError && (
                <p className="text-sm text-red-600">{campaignTypesError}</p>
              )}
              {campaignTypes.length === 0 && !campaignTypesLoading && !campaignTypesError && (
                <p className="text-sm text-gray-500">No campaign types available</p>
              )}
              {campaignTypes.length > 0 && (
                <p className="text-xs text-gray-500">
                  Choose the campaign type based on your needs. Each type has specific features and use cases.
                </p>
              )}
            </div>

            {/* Organization Hierarchy Selection */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-4">
                  Organization Hierarchy <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-4">
                  Select organizations in hierarchical order: EM → DM → CM → BM → AM
                </p>
                
                {/* EM Selection */}
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Executive Manager (EM)
                  </label>
                  <select
                    value={selectedEM}
                    onChange={(e) => setSelectedEM(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={organizationsLoading}
                  >
                    <option value="">
                      {organizationsLoading ? "Loading EM organizations..." : "Select EM"}
                    </option>
                    {emOrganizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* DM Selection */}
                {selectedEM && (
                  <div className="space-y-2 mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      District Manager (DM)
                    </label>
                    <select
                      value={selectedDM}
                      onChange={(e) => setSelectedDM(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 font-medium"
                    >
                      <option value="">Select DM</option>
                      {dmOrganizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* CM Selection */}
                {selectedDM && (
                  <div className="space-y-2 mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      City Manager (CM)
                    </label>
                    <select
                      value={selectedCM}
                      onChange={(e) => setSelectedCM(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 font-medium"
                    >
                      <option value="">Select CM</option>
                      {cmOrganizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* BM Selection */}
                {selectedCM && (
                  <div className="space-y-2 mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Branch Manager (BM)
                    </label>
                    <select
                      value={selectedBM}
                      onChange={(e) => setSelectedBM(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 font-medium"
                    >
                      <option value="">Select BM</option>
                      {bmOrganizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* AM Selection */}
                {selectedBM && (
                  <div className="space-y-2 mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Area Manager (AM)
                </label>
                <select
                      value={selectedAM}
                      onChange={(e) => setSelectedAM(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 font-medium"
                    >
                      <option value="">Select AM</option>
                      {amOrganizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                </select>
                  </div>
                )}

                {organizationsError && (
                  <p className="text-sm text-red-600">{organizationsError}</p>
                )}
              </div>
              </div>
              
            {/* Status Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900">
                  Initial Status
                </label>
                <select
                  name="status"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  defaultValue="ACTIVE"
                disabled={campaignStatusesLoading}
              >
                <option value="">
                  {campaignStatusesLoading ? "Loading statuses..." : "Select Status"}
                </option>
                {campaignStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
                </select>
              {campaignStatusesError && (
                <p className="text-sm text-red-600">{campaignStatusesError}</p>
              )}
              {campaignStatuses.length === 0 && !campaignStatusesLoading && !campaignStatusesError && (
                <p className="text-sm text-gray-500">No statuses available</p>
              )}
              {campaignStatuses.length > 0 && (
                <p className="text-xs text-gray-500">You can change this status later from campaign settings</p>
              )}
            </div>
          </div>
        </div>

        {/* Campaign Details Section */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-xs font-bold text-green-600">2</span>
              </div>
              Campaign Details
            </h2>
            <p className="text-sm text-gray-600 mt-1">Define the scope and focus of your campaign</p>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Target Audience and Legal Focus */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900">
                  Target Audience
                </label>
                <input
                  type="text"
                  name="targetAudience"
                  placeholder="e.g., Gaming addiction victims, Parents of affected children"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                  onBlur={handleTrimOnBlur}
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900">
                  Legal Focus Area
                </label>
                <input
                  type="text"
                  name="legalFocus"
                  placeholder="e.g., Mass tort litigation, Consumer protection"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                  onBlur={handleTrimOnBlur}
                />
              </div>
            </div>

            {/* Expected Volume */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-900">
                Expected Case Volume
              </label>
              <select
                name="expectedVolume"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900"
              >
                <option value="">Select expected volume</option>
                <option value="low">Low (1-50 cases)</option>
                <option value="medium">Medium (51-200 cases)</option>
                <option value="high">High (201-500 cases)</option>
                <option value="very-high">Very High (500+ cases)</option>
              </select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-900">
                Campaign Description
              </label>
              <textarea
                name="description"
                placeholder="Provide a comprehensive description of the campaign goals, legal strategy, target outcomes, and any other important details that will help team members understand the campaign scope..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none text-gray-900 placeholder-gray-500"
                onBlur={handleTrimOnBlur}
              />
              <p className="text-xs text-gray-500">This description will be visible to all team members working on this campaign</p>
            </div>
          </div>
        </div>

        {/* Timeline Section */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-xs font-bold text-purple-600">3</span>
              </div>
              Campaign Timeline
            </h2>
            <p className="text-sm text-gray-600 mt-1">Set the campaign duration and key milestones</p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900"
                  onBlur={handleTrimOnBlur}
                />
                <p className="text-xs text-gray-500">When should this campaign become active?</p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900">
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900"
                  onBlur={handleTrimOnBlur}
                />
                <p className="text-xs text-gray-500">Leave empty for ongoing campaigns</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              disabled={loading || organizationsLoading || campaignTypesLoading || campaignStatusesLoading}
              className="flex-1 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Campaign...
                </>
              ) : (
                <>
              <Plus className="w-5 h-5" />
              Create Campaign
                </>
              )}
            </button>
          </div>
          
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-blue-600">i</span>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900">What happens next?</p>
                <p className="text-xs text-blue-700 mt-1">
                  After creating the campaign, you'll be able to configure intake forms, assign agents, set up commission rates, and start tracking performance metrics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Success Modal */}
      {success && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl relative">
            {/* Close Button */}
            <button
              onClick={() => setSuccess(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <span className="text-gray-400 text-xl">×</span>
            </button>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Campaign Created Successfully!
              </h3>
              <p className="text-gray-600">
                Your campaign has been created and is ready for configuration.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl relative">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowErrorModal(false)
                setError(null)
              }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <span className="text-gray-400 text-xl">×</span>
            </button>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Failed to Create Campaign
              </h3>
              <p className="text-gray-600 mb-4">
                {error || "An unexpected error occurred while creating the campaign. Please try again."}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => {
                    setShowErrorModal(false)
                    setError(null)
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowErrorModal(false)
                    setError(null)
                    // Focus on the form to allow user to retry
                    const form = document.querySelector('form')
                    if (form) {
                      form.scrollIntoView({ behavior: 'smooth' })
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
